import CloudOffIcon from '@mui/icons-material/CloudOff';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { useNetworkStatus } from '@shared/hooks/useNetworkStatus';
import { useNotify } from '@shared/notifications';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Persistent banner shown while the browser is offline. Once we recover, the
 * banner collapses and a one-shot success toast is fired so users get a clear
 * "back online" signal — but only if they were actually offline first
 * (avoiding a noisy toast on first paint).
 */
export function OfflineBanner() {
  const { t } = useTranslation();
  const { online, wasOffline } = useNetworkStatus();
  const notify = useNotify();
  const lastOnline = useRef<boolean>(online);

  useEffect(() => {
    if (lastOnline.current === online) return;
    lastOnline.current = online;
    if (online && wasOffline) {
      notify.success(t('network.backOnline'));
    }
  }, [online, wasOffline, notify, t]);

  return (
    <Collapse in={!online} unmountOnExit>
      <Alert
        severity="warning"
        icon={<CloudOffIcon fontSize="inherit" />}
        role="status"
        aria-live="polite"
        sx={{ borderRadius: 0 }}
      >
        {t('network.offline')}
      </Alert>
    </Collapse>
  );
}
